import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Card, CardBody, CardHeader, Col, Input, Container } from "reactstrap";
import { H4 } from "../../AbstractElements";
import _ from "lodash";
import DataTable, { TableColumn } from "react-data-table-component";
import Loader from "../../assets/loader/loader.gif";
import { toast } from "react-toastify";
import { showToast } from "../../utils/toastUtils";

interface FilterOption {
  label: string;
  options: { id: string; label: string }[];
}

interface BasicDataTableProps {
  customStyle?:any;
  Title: string;
  columns?: TableColumn<any>[];
  data?: any;
  filterOptions?: FilterOption[];
  search?: boolean;
  headerButtonContent?: React.ReactNode;
  DateFilters?: boolean;
  tableHeaderButton?: boolean;
  SingleDateFilter?: boolean;
  headerContent?: React.ReactNode;
  showheaderContent?: boolean;
  headerButtonAfterSearchContent?: React.ReactNode;
  tableHeaderButtonAfterSearch?: boolean;
  TableBottomButton?: boolean;
  TableBottomButtonContent?: React.ReactNode;
  filterContentAfterSearch?: React.ReactNode;
  filterOptionsAfterSearch?: boolean;
  onChangePage?: any;
  onChangeRowsPerPage?: any;
  paginationTotalRows?: any;
  handleSearch?: any;
  searchValue?: any;
  topLeftAlignContent?: any;
  sortServer?: boolean;
  onSort?: (column: string, sortDirection: "ASC" | "DESC") => void;
  defaultSortFieldId?: string;
  defaultSortAsc?: boolean;
  sortColumn?: string;
  sortDirection?: "ASC" | "DESC";
  // ✅ new props
  showLimitButton?: boolean;
  LimitButtonContent?: React.ReactNode;
  tableBodyRef?: any;
  showSpinner?: boolean;
  noDataText?: string; // ✅ New prop for custom "no data" message
  filterFirstApporach?: boolean;
  SearchPlaceholder?: string;
  pagination?: boolean;
  dynamicHeightOnRowsPerPage?: boolean; // ✅ New prop
  rowHeight?: number;
  minHeightOfTable?:undefined | number;
  paginationRowPerPageOptions?:any
}

  <style>
    {`
      .rdt_TableHeadRow div[role="columnheader"]:first-child {
        position: sticky;
        left: 0;
        background-color: #fff;
        z-index: 4;
      }
    `}
  </style>

const BasicDataTable: React.FC<BasicDataTableProps> = ({
  minHeightOfTable= undefined,
  filterFirstApporach = true,
  Title,
  columns,
  data,
  search = true,
  headerContent,
  headerButtonAfterSearchContent,
  tableHeaderButtonAfterSearch,
  TableBottomButton,
  TableBottomButtonContent,
  filterContentAfterSearch,
  filterOptionsAfterSearch,
  onChangePage,
  onChangeRowsPerPage,
  paginationTotalRows,
  handleSearch = () => {},
  searchValue,
  topLeftAlignContent = false,
  sortServer = false,
  onSort,
  defaultSortFieldId,
  defaultSortAsc = true,
  sortColumn,
  sortDirection,
  showLimitButton,
  LimitButtonContent,
  tableBodyRef,
  showSpinner = false,
  noDataText = "There are no records to display", // ✅ default
  SearchPlaceholder = "Search...",
  pagination = true,
  dynamicHeightOnRowsPerPage = true, // ✅ New prop
  paginationRowPerPageOptions=[10, 20, 50, 100],
  rowHeight,
   customStyle={
                      headCells: {
                        style: {
                          fontSize: 14,
                          fontWeight: "bold",
                          zIndex: 2, // keep it above body cells
                        },
                      },
                      cells: {
                        style: {
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                        },
                      },
                    }
}) => {
  // 🔹 Local state for immediate input update
  const [localSearch, setLocalSearch] = useState(searchValue || "");

  // const handleInputChange = useCallback(
  //   _.debounce((value: any) => {
  //     handleSearch(value);
  //   }, 700),
  //   []
  // );

  const [progressPending, setProgressPending] = useState(true);
  const [showScrollButton, setShowScrollButton] = useState(false);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [currentSortColumn, setCurrentSortColumn] = useState<
    string | undefined
  >(sortColumn);
  const [currentSortDirection, setCurrentSortDirection] = useState<
    "ASC" | "DESC" | undefined
  >(sortDirection);

  const tableRef = useRef<HTMLDivElement>(null);
  const scrollContainer = tableRef.current?.querySelector(".rdt_TableBody");

  useEffect(() => {
    setLocalSearch(searchValue || ""); // sync if Redux updates externally
  }, [searchValue]);

  // 🔹 Debounced search trigger for Redux
  const debouncedSearch = useCallback(
    _.debounce((value: string) => {
      handleSearch(value);
    }, 700),
    []
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setLocalSearch(value); // update instantly for UI
    const newSearch = value.trim();
    debouncedSearch(newSearch); // trigger redux update

  };

  const scrollToBottom = useCallback(() => {
    const container = tableRef.current?.querySelector(".rdt_TableBody");
    if (container) {
      container.scrollTo({
        top: container.scrollHeight,
        behavior: "smooth",
      });
    }
  }, []);

  const customStyles = {
  headCells: {
    style: {
      fontSize: 14,
      fontWeight: 'bold',
      '&:first-of-type': {
        position: 'sticky',
        left: 0,
        backgroundColor: '#fff',
        zIndex: 5,
        borderRight: '1px solid #ddd'
      }
    }
  },
  cells: {
    style: {
      whiteSpace: 'normal',
      wordBreak: 'break-word',
      '&:first-of-type': {
        position: 'sticky',
        left: 0,
        backgroundColor: '#fff',
        zIndex: 4,
        borderRight: '1px solid #ddd'
      }
    }
  }
};


  useEffect(() => {
    if (!data ) {
      setProgressPending(true);
      const timer = setTimeout(() => {
        setProgressPending(false);
        // showToast.info("Data not available. Please try again later.");
      }, 5000);
      return () => clearTimeout(timer);
    } else {
      setProgressPending(false);
    }
  }, [data]);

  //   const calculatedHeight = useMemo(() => {
  //   if (!dynamicHeightOnRowsPerPage) return "350px"; // fallback default

  //   switch (rowsPerPage) {
  //     case 10:
  //       return "350px";
  //     case 20:
  //       return "600px";
  //     case 50:
  //       return "1000px";
  //     case 100:
  //       return "1800px";
  //     default:
  //       return "350px";
  //   }
  // }, [rowsPerPage, dynamicHeightOnRowsPerPage]);

  const calculatedHeight = useMemo(() => {
    if (!dynamicHeightOnRowsPerPage) return "350px";

    const estimatedRowHeight = rowHeight || 50; // px
    const headerHeight = 56; // approx px
    const padding = 20; // extra spacing

    // total = header + (rows * rowHeight) + padding
    return `${headerHeight + rowsPerPage * estimatedRowHeight + padding}px`;
  }, [rowsPerPage, dynamicHeightOnRowsPerPage, rowHeight]);

  // Sync sorting props if they change
  useEffect(() => {
    if (sortColumn) setCurrentSortColumn(sortColumn);
    if (sortDirection) setCurrentSortDirection(sortDirection);
  }, [sortColumn, sortDirection]);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      const container = tableRef.current?.querySelector(".rdt_TableBody");
      if (!container) return;

      const isScrollable = container.scrollHeight > container.clientHeight;
      const isAtBottom =
        container.scrollTop + container.clientHeight >=
        container.scrollHeight - 10;

      setShowScrollButton(isScrollable && !isAtBottom);

      container.addEventListener("scroll", handleScroll);
    });

    const handleScroll = () => {
      const container = tableRef.current?.querySelector(".rdt_TableBody");
      if (!container) return;

      const isScrollable = container.scrollHeight > container.clientHeight;
      const isAtBottom =
        container.scrollTop + container.clientHeight >=
        container.scrollHeight - 10;

      setShowScrollButton(isScrollable && !isAtBottom);
    };

    const container = tableRef.current;
    if (container) {
      observer.observe(container, { childList: true, subtree: true });
    }

    window.addEventListener("resize", handleScroll);

    return () => {
      observer.disconnect();
      const innerContainer = tableRef.current?.querySelector(".rdt_TableBody");
      if (innerContainer)
        innerContainer.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleScroll);
    };
  }, [data]);

  return (
    <>
      <style>
        {`
    #row_create::-webkit-scrollbar {
      height: 8px;
    }

    #row_create::-webkit-scrollbar-track {
      background: #f0f0f0;
    }

    #row_create::-webkit-scrollbar-thumb {
      background-color: #e0e0e0;
      border-radius: 4px;
    }

    /* Remove left/right scrollbar arrows in WebKit browsers (Chrome, Safari, Edge) */
    #row_create::-webkit-scrollbar-button {
      display: none;
      width: 0;
      height: 0;
    }

    /* For Firefox */
    #row_create {
      scrollbar-width: thin; /* makes scrollbar smaller */
      scrollbar-color: #e0e0e0 #f0f0f0; /* thumb and track colors */
    }
  `}
      </style>

      <Col sm="12">
        <Card className="border-0" style={{ marginBottom: 0 }}>
          <CardHeader
            className="pb-0 card-no-border"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <H4>{Title}</H4>
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              {search && (
                <Input
                  placeholder={SearchPlaceholder}
                  onChange={handleInputChange} // pass event directly
                  type="search"
                  value={localSearch} // ✅ now local state
                  style={{
                    width: "250px",
                    maxWidth: "100%",
                    border: "none",
                    borderRadius: 7,
                    backgroundColor: "#f0f0f0",
                  }}
                />
              )}

              {topLeftAlignContent && <>{topLeftAlignContent}</>}
            </div>
          </CardHeader>

          <Container fluid className="basicinit-page">
            <CardBody>
              <div
                className="table-controls"
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  gap: "10px",
                  justifyContent: "flex-end",
                }}
              >
                {headerContent && <>{headerContent}</>}
              </div>

              <div
                className="table-responsive theme-scrollbar"
                id="row_create"
                ref={tableBodyRef}
                style={{position: "relative",...(minHeightOfTable && { height: minHeightOfTable }),}}
              >
                {filterFirstApporach ? (
                  <DataTable
                    paginationServer
                    responsive={true} // ✅ optional
                    paginationTotalRows={paginationTotalRows}
                    onChangePage={onChangePage}
                    paginationRowsPerPageOptions={paginationRowPerPageOptions}
                    onChangeRowsPerPage={(newPerPage, page) => {
                      setRowsPerPage(newPerPage); // ✅ update rows per page
                      if (onChangeRowsPerPage)
                        onChangeRowsPerPage(newPerPage, page);
                    }}
                    data={data}
                    columns={columns || []}
                    highlightOnHover
                    striped
                    pagination={pagination}
                    fixedHeader
                    fixedHeaderScrollHeight={calculatedHeight}
                    progressPending={showSpinner || progressPending}
                    noDataComponent={
                      <div
                        style={{
                          padding: "20px",
                          textAlign: "center",
                          color: "#888",
                        }}
                      >
                        {noDataText}
                      </div>
                    }
                    progressComponent={
                      <div
                        style={{
                          padding: "20px",
                          textAlign: "center",
                          width: "100%",
                        }}
                      >
                        <img
                          src={Loader}
                          alt="Loading..."
                          style={{ height: 40 }}
                        />
                      </div>
                    }
                    customStyles={customStyle}
                    className="display dataTable"
                    sortServer={sortServer}
                    onSort={(column, sortDirection) => {
                      const sortKey =
                        column.sortField ||
                        (typeof column.selector === "string"
                          ? column.selector
                          : typeof column.name === "string"
                          ? column.name
                          : undefined);

                      if (typeof sortKey === "string") {
                        const upperDirection = sortDirection.toUpperCase() as
                          | "ASC"
                          | "DESC";

                        setCurrentSortColumn(sortKey);
                        setCurrentSortDirection(upperDirection);

                        if (onSort) {
                          onSort(sortKey, upperDirection);
                        }
                      }
                    }}
                    defaultSortFieldId={defaultSortFieldId || currentSortColumn}
                    defaultSortAsc={
                      defaultSortAsc ?? currentSortDirection === "ASC"
                    }
                  />
                ) : (
                  <div style={{ textAlign: "center", color: "red" }}>
                    <strong>Select filter first</strong>
                  </div>
                )}

                {showScrollButton && (
                  <button
                    onClick={scrollToBottom}
                    style={{
                      position: "absolute",
                      right: 20,
                      bottom: 20,
                      backgroundColor: "#007bff",
                      color: "#fff",
                      border: "none",
                      borderRadius: "50%",
                      width: 40,
                      height: 40,
                      boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
                      cursor: "pointer",
                      zIndex: 1000,
                    }}
                    title="Scroll to Bottom"
                  >
                    ↓
                  </button>
                )}
                {showLimitButton && (
                  <div style={{ marginTop: "15px", textAlign: "center" }}>
                    {LimitButtonContent}
                  </div>
                )}
              </div>

              {TableBottomButton && <>{TableBottomButtonContent}</>}
            </CardBody>
          </Container>
        </Card>
      </Col>
    </>
  );
  /* index.css or App.css */

};

export default BasicDataTable;

// import React, { useCallback, useEffect, useMemo, useState } from "react";
// import { Card, CardBody, CardHeader, Col, Input, Container } from "reactstrap";
// import { H4 } from "../../AbstractElements";
// import _ from "lodash";
// import DataTable, {
//   TableColumn,
//   TableStyles,
// } from "react-data-table-component";
// import RoundedDropdown from "../../Component/BasicComponents/Dropdowns/RoundedDropdown";
// import Loader from "../../assets/loader/loader.gif";
// import { toast } from "react-toastify";

// interface FilterOption {
//   label: string;
//   options: { id: string; label: string }[];
// }

// interface BasicDataTableProps {
//   Title: string;
//   columns?: TableColumn<any>[];
//   data?: any;
//   filterOptions?: FilterOption[];
//   search?: boolean;
//   headerButtonContent?: React.ReactNode;
//   DateFilters?: boolean;
//   tableHeaderButton?: boolean;
//   SingleDateFilter?: boolean; // New prop for single date filter
//   headerContent?: React.ReactNode;
//   // showtableHeader?:boolean
//   showheaderContent?: boolean;
//   headerButtonAfterSearchContent?: React.ReactNode;
//   tableHeaderButtonAfterSearch?: boolean;
//   TableBottomButton?: boolean;
//   TableBottomButtonContent?: React.ReactNode;
//   filterContentAfterSearch?: React.ReactNode;
//   filterOptionsAfterSearch?: boolean;
//   onChangePage?: any;
//   onChangeRowsPerPage?: any;
//   paginationTotalRows?: any;
//   handleSearch?: any;
//   searchValue?: any;
//   topLeftAlignContent?: any;
//   sortServer?: boolean;
//   onSort?: (column: string, sortDirection: "asc" | "desc") => void;
//   defaultSortFieldId?: string;
//   defaultSortAsc?: boolean;
// }

// const BasicDataTable: React.FC<BasicDataTableProps> = ({
//   Title,
//   columns,
//   data,
//   search = true,
//   headerContent,
//   // showheaderContent,
//   headerButtonAfterSearchContent,
//   tableHeaderButtonAfterSearch,
//   TableBottomButton,
//   TableBottomButtonContent,
//   filterContentAfterSearch,
//   filterOptionsAfterSearch,
//   onChangePage,
//   onChangeRowsPerPage,
//   paginationTotalRows,
//   handleSearch = () => {},
//   searchValue,
//   topLeftAlignContent = false,
//   sortServer = false,
//   onSort,
//   defaultSortFieldId,
//   defaultSortAsc = true,
// }) => {
//   const handleInputChange = useCallback(
//     _.debounce((value: any) => {
//       handleSearch(value);
//     }, 700), // Delay of 700ms
//     []
//   );
//   const [progressPending, setProgressPending] = useState(true);
//   useEffect(() => {
//     if (!data || data.length === 0) {
//       setProgressPending(true);

//       const timer = setTimeout(() => {
//         setProgressPending(false);
//         toast("Data not available. Please try again later.");
//       }, 5000);
//       return () => clearTimeout(timer);
//     } else {
//       setProgressPending(false);
//     }
//   }, [data]);

//   return (
//     <Col sm="12">
//       <Card className="border-0">
//         <CardHeader
//           className="pb-0 card-no-border"
//           style={{
//             display: "flex",
//             alignItems: "center",
//             justifyContent: "space-between",
//           }}
//         >
//           <H4>{Title}</H4>
//           {search && (
//             <Input
//               placeholder="Search..."
//               onChange={(e: any) => {
//                 handleInputChange(e.target.value);
//               }}
//               type="search"
//               value={searchValue}
//               style={{
//                 width: "250px",
//                 maxWidth: "100%",
//                 border: "none",
//                 borderRadius: 7,
//                 marginRight: "20px",
//               }}
//             />
//           )}
//           {topLeftAlignContent && <>{topLeftAlignContent}</>}
//         </CardHeader>
//         <Container fluid className="basicinit-page">
//           <CardBody>
//             <div
//               className="table-controls"
//               style={{
//                 display: "flex",
//                 flexWrap: "wrap",
//                 alignItems: "center",
//                 gap: "10px",
//                 justifyContent: "flex-end",
//               }}
//             >
//               {headerContent && <>{headerContent}</>}
//             </div>
//             <div className="table-responsive theme-scrollbar" id="row_create">
//               <DataTable
//                 // progressPending={data &&data.length<=0}
//                 paginationServer
//                 paginationTotalRows={paginationTotalRows}
//                 onChangePage={onChangePage}
//                 paginationRowsPerPageOptions={[10, 15, 20, 30]}
//                 onChangeRowsPerPage={onChangeRowsPerPage}
//                 data={data}
//                 columns={columns || []}
//                 highlightOnHover
//                 striped
//                 pagination
//                 fixedHeader
//                 fixedHeaderScrollHeight="350px"
//                 progressPending={progressPending}
//                 progressComponent={
//                   <div
//                     style={{
//                       padding: "20px",
//                       textAlign: "center",
//                       width: "100%",
//                     }}
//                   >
//                     <img src={Loader} alt="Loading..." style={{ height: 40 }} />
//                   </div>
//                 }
//                 customStyles={{
//                   headCells: { style: { fontSize: 14, fontWeight: "bold" } },
//                   cells: {
//                     style: { whiteSpace: "normal", wordBreak: "break-word" },
//                   },
//                 }}
//                 className="display dataTable"
//                 sortServer={sortServer}
//                 onSort={(column, sortDirection) => {
//                   if (onSort) {
//                     const sortKey =
//                       column.sortField || // <--- Use this
//                       (typeof column.selector === "string"
//                         ? column.selector
//                         : column.name);
//                     onSort(sortKey as string, sortDirection);
//                   }
//                 }}
//                 defaultSortFieldId={defaultSortFieldId}
//                 defaultSortAsc={defaultSortAsc}
//               />
//             </div>

//             {TableBottomButton && <>{TableBottomButtonContent}</>}
//           </CardBody>
//         </Container>
//       </Card>
//     </Col>
//   );
// };

// export default BasicDataTable;


